/**
 * Iter C.2.1.e — Special Offer Editor (right-column card on Proposal Detail).
 *
 * Authoring surface for the commercial-communication primitive. Per Rahul's
 * 2026-02 decisions:
 *   • One offer per proposal (grand-total scope).
 *   • Client-facing `label` (free-text) — verbatim on PDFs.
 *   • Internal `reason_code` (closed 5-code enum) — NEVER on client artifacts.
 *   • Percentage OR FixedAmount; net = standard - discount; GST on net.
 *   • Editable only on Draft proposals. Once Sent/Approved/Converted the
 *     card switches to read-only (SpecialOfferDisplay).
 *
 * Backend contract:
 *   POST   /api/billing/proposal/{id}/special-offer    (apply / replace)
 *   DELETE /api/billing/proposal/{id}/special-offer    (remove)
 *   GET    /api/billing/proposal/_meta/special-offer/reason-codes
 */
import React, { useEffect, useMemo, useState } from "react";
import { Tag, TrendDown, Lock, Plus, X, ArrowClockwise, ClockClockwise } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { RoleGuard } from "../RoleGuard";

const REASON_CODE_FALLBACK = [
    "Strategic", "Relationship", "Competitive", "Promotional", "Existing-Client",
];

function defaultClientLabel(clientName) {
    if (!clientName) return "Special Offer";
    const trimmed = clientName.trim();
    return trimmed ? `Special Offer for ${trimmed}` : "Special Offer";
}

export function SpecialOfferEditor({ proposal, editable, onChanged }) {
    const offer = proposal?.special_offer || null;
    const summary = proposal?.special_offer_summary || null;
    const clientName = proposal?.client?.name || "";
    const currency = proposal?.currency;

    const standard = Number(summary?.standard ?? 0);
    const gstFromLineItems = useMemo(() => {
        // Use the GST captured against each line item — Special Offer is
        // pre-tax (Section 15(3)(a) CGST), so the same per-line gst_percent
        // applies but to a proportionally-reduced taxable base. For the
        // editor preview, approximate with a blended GST rate against the
        // standard value — close enough for the operator's visual check.
        const versions = proposal?.versions || [];
        if (!versions.length) return 0;
        const approvedId = proposal?.approved_version_id;
        const v = approvedId ? versions.find((x) => x.id === approvedId) : versions[versions.length - 1];
        if (!v) return 0;
        let subtotal = 0;
        let gst = 0;
        for (const li of v.line_items || []) {
            const amt = Number(li.amount || 0);
            const pct = li.gst_applicable === false ? 0 : Number(li.gst_percent || 0);
            subtotal += amt;
            gst += amt * pct / 100;
        }
        return subtotal > 0 ? gst / subtotal : 0;  // effective rate
    }, [proposal]);

    // Form state — pre-fills from the persisted offer when editing.
    const [enabled, setEnabled] = useState(Boolean(offer));
    const [label, setLabel] = useState(offer?.label || defaultClientLabel(clientName));
    const [discountType, setDiscountType] = useState(offer?.discount_type || "Percentage");
    const [discountValue, setDiscountValue] = useState(
        offer?.discount_value != null ? String(offer.discount_value) : ""
    );
    const [reasonCode, setReasonCode] = useState(offer?.reason_code || "Relationship");
    const [reasonCodeOptions, setReasonCodeOptions] = useState(REASON_CODE_FALLBACK);
    const [saving, setSaving] = useState(false);

    // Re-hydrate when the proposal payload changes underneath the form
    // (e.g. after another card triggers a `load()` refresh).
    useEffect(() => {
        setEnabled(Boolean(offer));
        setLabel(offer?.label || defaultClientLabel(clientName));
        setDiscountType(offer?.discount_type || "Percentage");
        setDiscountValue(offer?.discount_value != null ? String(offer.discount_value) : "");
        setReasonCode(offer?.reason_code || "Relationship");
    }, [proposal?.id, offer?.applied_at, offer?.discount_value, offer?.label, offer?.reason_code, clientName, offer]);

    // Load reason-code list once (lightweight; degrades gracefully).
    useEffect(() => {
        let alive = true;
        api.get("/billing/proposal/_meta/special-offer/reason-codes")
            .then((r) => { if (alive && r.data?.reason_codes?.length) setReasonCodeOptions(r.data.reason_codes); })
            .catch(() => { /* fallback to constants */ });
        return () => { alive = false; };
    }, []);

    // Live preview math — server is the source of truth on save; this
    // mirrors the server formula so the operator sees what they'll get.
    const preview = useMemo(() => {
        const numericValue = Number(discountValue || 0);
        let discount = 0;
        if (discountType === "Percentage") {
            if (numericValue >= 0 && numericValue <= 100) discount = standard * numericValue / 100;
        } else if (discountType === "FixedAmount") {
            if (numericValue >= 0) discount = Math.min(numericValue, standard);
        }
        const net = Math.max(0, standard - discount);
        const gstOnNet = net * gstFromLineItems;
        const grandTotal = net + gstOnNet;
        return {
            standard: Math.round(standard * 100) / 100,
            discount: Math.round(discount * 100) / 100,
            net: Math.round(net * 100) / 100,
            gstOnNet: Math.round(gstOnNet * 100) / 100,
            grandTotal: Math.round(grandTotal * 100) / 100,
        };
    }, [discountValue, discountType, standard, gstFromLineItems]);

    const resetLabelToDefault = () => setLabel(defaultClientLabel(clientName));

    const handleApply = async () => {
        if (!discountValue || Number(discountValue) <= 0) {
            toast.error("Enter a discount value greater than 0");
            return;
        }
        if (!label.trim()) {
            toast.error("Client-facing label is required");
            return;
        }
        setSaving(true);
        try {
            await api.post(`/billing/proposal/${proposal.id}/special-offer`, {
                label: label.trim(),
                discount_type: discountType,
                discount_value: Number(discountValue),
                reason_code: reasonCode,
            });
            toast.success(offer ? "Special Offer updated" : "Special Offer applied");
            if (onChanged) onChanged();
        } catch (e) {
            toast.error(formatApiError(e, "Failed to apply offer"));
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        if (!window.confirm("Remove the Special Offer from this proposal? Standard pricing will apply.")) return;
        setSaving(true);
        try {
            await api.delete(`/billing/proposal/${proposal.id}/special-offer`);
            toast.success("Special Offer removed");
            setEnabled(false);
            if (onChanged) onChanged();
        } catch (e) {
            toast.error(formatApiError(e, "Failed to remove offer"));
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------

    // Disabled card (no offer applied) on a non-Draft proposal — show
    // the empty-state message but no apply CTA.
    if (!editable && !offer) {
        return (
            <div className="reedos-card" data-testid="special-offer-card-disabled">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                    <Tag size={14} className="text-slate-400" />
                    <span className="reedos-eyebrow text-slate-500">Special Offer</span>
                </div>
                <div className="px-5 py-4 text-sm text-slate-500">
                    No special offer applied — proposal at standard commercial value.
                </div>
            </div>
        );
    }

    // Frozen state — Sent / Approved / Converted with offer present.
    // Render the offer in read-only mode (frozen indicator).
    if (!editable && offer) {
        return (
            <div className="reedos-card" data-testid="special-offer-card-frozen">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Lock size={14} className="text-amber-600" weight="fill" />
                        <span className="reedos-eyebrow text-slate-600">Special Offer · Frozen</span>
                    </div>
                    <span className="text-[10px] text-slate-400">
                        Applied {offer.applied_at?.substring(0, 10)}
                    </span>
                </div>
                <div className="px-5 py-4 space-y-3">
                    <div>
                        <div className="reedos-eyebrow text-slate-500 mb-0.5">Client-facing label</div>
                        <div className="text-sm font-medium text-slate-900" data-testid="special-offer-frozen-label">
                            {offer.label}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                            <div className="reedos-eyebrow text-slate-500 mb-0.5">Discount</div>
                            <div className="text-sm">
                                {offer.discount_type === "Percentage"
                                    ? `${offer.discount_value}%`
                                    : "Fixed"}
                                {" "}
                                <span className="text-slate-500">— {formatINR(offer.discount_amount, currency)}</span>
                            </div>
                        </div>
                        <div>
                            <div className="reedos-eyebrow text-slate-500 mb-0.5">Reason · internal</div>
                            <div className="text-sm font-medium text-indigo-700" data-testid="special-offer-frozen-reason">
                                {offer.reason_code}
                            </div>
                        </div>
                    </div>
                    <ThreeRowBlock
                        standard={Number(offer.standard_value_at_apply || 0)}
                        discount={Number(offer.discount_amount || 0)}
                        net={Number(offer.net_value_at_apply || 0)}
                        currency={currency}
                    />
                </div>
            </div>
        );
    }

    // Editable state — Draft proposal. Default-state CTA when no offer applied yet.
    if (!enabled) {
        return (
            <RoleGuard permission="proposal:write">
                <div className="reedos-card" data-testid="special-offer-card-empty">
                    <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
                        <Tag size={14} className="text-slate-500" />
                        <span className="reedos-eyebrow text-slate-600">Special Offer</span>
                    </div>
                    <div className="px-5 py-5 text-center">
                        <div className="text-sm text-slate-600 mb-3">
                            This proposal has no special offer applied.
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEnabled(true)}
                            data-testid="special-offer-apply-cta"
                        >
                            <Plus size={12} className="mr-1.5" />
                            Apply Special Offer
                        </Button>
                    </div>

                    {/* C.2.1.e — Renewal indicator. Surfaces the most recent
                        Special Offer the same client received via prior PO
                        cycles (within 24mo). Passive — never auto-applies.
                        Re-apply seeds label / reason / discount onto this
                        proposal but keeps Standard pricing intact until the
                        operator confirms. */}
                    {proposal?.prior_offer_hint && (
                        <PriorOfferHintBanner
                            hint={proposal.prior_offer_hint}
                            onReApply={async () => {
                                setSaving(true);
                                try {
                                    await api.post(`/billing/proposal/${proposal.id}/special-offer`, {
                                        label: proposal.prior_offer_hint.label,
                                        discount_type: proposal.prior_offer_hint.discount_type,
                                        discount_value: proposal.prior_offer_hint.discount_value,
                                        reason_code: proposal.prior_offer_hint.reason_code,
                                    });
                                    toast.success("Prior offer re-applied");
                                    if (onChanged) onChanged();
                                } catch (e) {
                                    toast.error(formatApiError(e, "Re-apply failed"));
                                } finally {
                                    setSaving(false);
                                }
                            }}
                            disabled={saving}
                        />
                    )}
                </div>
            </RoleGuard>
        );
    }

    // Editor form.
    return (
        <RoleGuard permission="proposal:write">
            <div className="reedos-card" data-testid="special-offer-card-editor">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Tag size={14} className="text-amber-600" />
                        <span className="reedos-eyebrow text-slate-700">Special Offer</span>
                        {offer && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                Active
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {offer && (
                            <button
                                type="button"
                                onClick={handleRemove}
                                disabled={saving}
                                className="text-[11px] text-rose-600 hover:text-rose-800 font-medium inline-flex items-center gap-1"
                                data-testid="special-offer-remove-btn"
                            >
                                <X size={11} />
                                Remove
                            </button>
                        )}
                        {!offer && (
                            <button
                                type="button"
                                onClick={() => setEnabled(false)}
                                className="text-[11px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-1"
                                data-testid="special-offer-cancel-btn"
                            >
                                <X size={11} />
                                Cancel
                            </button>
                        )}
                    </div>
                </div>
                <div className="px-5 py-4 space-y-4">
                    {/* Client-facing label */}
                    <div>
                        <div className="reedos-eyebrow text-slate-500 mb-1">
                            Client-facing label
                            <span className="font-normal text-slate-400 normal-case tracking-normal ml-1">
                                (visible on PDF)
                            </span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Input
                                value={label}
                                onChange={(e) => setLabel(e.target.value.slice(0, 120))}
                                placeholder="Special Offer for ..."
                                className="h-9 text-sm"
                                data-testid="special-offer-label-input"
                            />
                            <Button
                                type="button" variant="ghost" size="sm"
                                onClick={resetLabelToDefault}
                                title="Reset to default"
                                data-testid="special-offer-label-reset"
                            >
                                <ArrowClockwise size={13} />
                            </Button>
                        </div>
                    </div>

                    {/* Internal reason code */}
                    <div>
                        <div className="reedos-eyebrow text-slate-500 mb-1">
                            Internal reason code
                            <span className="font-normal text-indigo-600 normal-case tracking-normal ml-1">
                                (never shown on client artifacts)
                            </span>
                        </div>
                        <select
                            value={reasonCode}
                            onChange={(e) => setReasonCode(e.target.value)}
                            className="w-full h-9 text-sm rounded-md border border-slate-300 px-3 bg-white"
                            data-testid="special-offer-reason-select"
                        >
                            {reasonCodeOptions.map((rc) => (
                                <option key={rc} value={rc}>{rc}</option>
                            ))}
                        </select>
                    </div>

                    {/* Discount type + value */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="reedos-eyebrow text-slate-500 mb-1">Discount type</div>
                            <div className="flex gap-1.5 text-[11px]">
                                {["Percentage", "FixedAmount"].map((t) => (
                                    <button
                                        type="button"
                                        key={t}
                                        onClick={() => setDiscountType(t)}
                                        className={`flex-1 h-9 rounded-md border transition-colors ${
                                            discountType === t
                                                ? "bg-slate-900 text-white border-slate-900"
                                                : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                                        }`}
                                        data-testid={`special-offer-type-${t.toLowerCase()}`}
                                    >
                                        {t === "Percentage" ? "%" : "Fixed ₹"}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="reedos-eyebrow text-slate-500 mb-1">Discount value</div>
                            <div className="relative">
                                <Input
                                    type="number"
                                    min={0}
                                    step={discountType === "Percentage" ? "0.5" : "1"}
                                    value={discountValue}
                                    onChange={(e) => setDiscountValue(e.target.value)}
                                    placeholder={discountType === "Percentage" ? "e.g. 10" : "e.g. 50000"}
                                    className="h-9 text-sm pr-10"
                                    data-testid="special-offer-value-input"
                                />
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">
                                    {discountType === "Percentage" ? "%" : "₹"}
                                </div>
                            </div>
                            {preview.discount > 0 && (
                                <div className="text-[10px] text-slate-500 mt-1">
                                    = {formatINR(preview.discount, currency)} off
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Live preview block */}
                    <div className="pt-2">
                        <ThreeRowBlock
                            standard={preview.standard}
                            discount={preview.discount}
                            net={preview.net}
                            gstOnNet={preview.gstOnNet}
                            grandTotal={preview.grandTotal}
                            currency={currency}
                            highlight
                        />
                    </div>

                    <Button
                        type="button"
                        onClick={handleApply}
                        disabled={saving || !discountValue}
                        className="w-full"
                        data-testid="special-offer-save-btn"
                    >
                        <TrendDown size={13} className="mr-1.5" weight="bold" />
                        {saving ? "Saving..." : offer ? "Update Offer" : "Apply Offer"}
                    </Button>
                </div>
            </div>
        </RoleGuard>
    );
}

/** C.2.1.e — Renewal-indicator banner. Surfaces the most recent prior
 * Special Offer the same client received (within 24mo) as a passive prompt.
 * Operator clicks `Re-apply` to seed the same label / reason / discount.
 * Per Rahul's Decision 3 (2026-02), this never auto-applies — renewal
 * cycles deliberately seed from Standard so margin discipline is preserved.
 */
function PriorOfferHintBanner({ hint, onReApply, disabled }) {
    if (!hint) return null;
    const valueLabel =
        hint.discount_type === "Percentage"
            ? `${hint.discount_value}%`
            : `Fixed ₹${Number(hint.discount_value || 0).toLocaleString("en-IN")}`;
    return (
        <div
            className="mx-5 mb-4 mt-1 rounded-md border border-amber-200 bg-amber-50/70 px-4 py-3"
            data-testid="prior-offer-hint-banner"
        >
            <div className="flex items-start gap-2.5">
                <ClockClockwise size={15} className="text-amber-700 mt-0.5 shrink-0" weight="duotone" />
                <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800 mb-1">
                        Prior cycle had a Special Offer
                    </div>
                    <div className="text-sm text-slate-800 mb-2">
                        {hint.source_po_number ? (
                            <span className="font-medium">{hint.source_po_number}</span>
                        ) : null}
                        {hint.source_po_number ? " · " : ""}
                        <span className="font-medium">{valueLabel}</span>
                        {" "}
                        <span className="text-slate-600">— "{hint.label}"</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mb-3" data-testid="prior-offer-hint-reason">
                        Internal reason: <span className="font-medium text-indigo-700">{hint.reason_code}</span>
                        {" · "}
                        applied {hint.applied_at?.substring(0, 10)}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={onReApply}
                        disabled={disabled}
                        data-testid="prior-offer-reapply-btn"
                    >
                        <ArrowClockwise size={12} className="mr-1.5" />
                        Re-apply same offer
                    </Button>
                </div>
            </div>
        </div>
    );
}


/** Shared three-row commercial-summary block. Re-used by the editor preview
 * AND the frozen-state read-only display so the math always renders
 * identically. ``highlight=true`` adds a subtle amber backdrop for the
 * live editing preview. */
function ThreeRowBlock({ standard, discount, net, gstOnNet, grandTotal, currency, highlight = false }) {
    return (
        <div
            className={`rounded-md border ${highlight ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-slate-50/50"} px-4 py-3 text-xs space-y-1.5`}
            data-testid="special-offer-three-row"
        >
            <div className="flex justify-between text-slate-700">
                <span>Standard Commercial Value</span>
                <span className="reedos-data">{formatINR(standard, currency)}</span>
            </div>
            <div className="flex justify-between text-rose-700 font-medium">
                <span>Less: Special Offer</span>
                <span className="reedos-data">−{formatINR(discount, currency)}</span>
            </div>
            <div className="flex justify-between border-t border-slate-300 pt-1.5 text-slate-900 font-display font-semibold">
                <span>Net Commercial Value</span>
                <span className="reedos-data">{formatINR(net, currency)}</span>
            </div>
            {gstOnNet != null && (
                <>
                    <div className="flex justify-between text-slate-600 pt-0.5">
                        <span>GST on Net (effective)</span>
                        <span className="reedos-data">+{formatINR(gstOnNet, currency)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-400 pt-1.5 text-slate-900 font-display font-bold text-sm">
                        <span>Grand Total</span>
                        <span className="reedos-data">{formatINR(grandTotal, currency)}</span>
                    </div>
                </>
            )}
        </div>
    );
}

export default SpecialOfferEditor;
